import { Box, Link, Typography } from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useGetApiDeliverablesByIdQuery } from '../../../../app/athenaApiGen';
import { RootState } from '../../../../app/store';
import Downloads from '../../../../components/ViewPanel/components/Downloads/Downloads';
import { formViewRouteByAbbreviation } from '../../../formSchemaRegistry';
import {
  ViewPanelContainer,
  ViewPanelContentContainer,
} from '../../../../components/ViewPanel/StyledComponents';
import { isActionEnabled, isFormTypeAvailable } from '../../../../pages/utilities';
import { DeliverableDetailsContainer } from '../../StyledComponents';
import Header from './components/Header';
dayjs.extend(utc);

type TFormsActions = {
  ctp: boolean;
  tr: boolean;
  rip: boolean;
  tar: boolean;
  pnr: boolean;
};

const ViewPanel = () => {
  const navigate = useNavigate();
  const [isViewFormsEnabled, setisViewFormsEnabled] = useState<TFormsActions>({
    ctp: false,
    tr: false,
    rip: false,
    tar: false,
    pnr: false,
  });
  const { isViewPanelOpen, selectedRowId, allowedActions, expandedPanel } =
    useSelector((state: RootState) => state.deliverables);
  const availableForms = useSelector(
    (state: RootState) => state.user.availableForms,
  );

  const { data } = useGetApiDeliverablesByIdQuery({ id: selectedRowId || '' });
  const deliverableAbbreviation = (
    data?.deliverableType?.abbreviation ?? ''
  ).toLowerCase();

  const isForm = ['ctp', 'pnr', 'rip', 'tar', 'tr'].includes(
    deliverableAbbreviation,
  );

  useEffect(() => {
    if (isForm) {
      const formsActions = {
        ctp:
          !!isActionEnabled(allowedActions, 'create-ctp') &&
          isFormTypeAvailable(availableForms, 'ctp'),
        tr:
          !!isActionEnabled(allowedActions, 'create-tr') &&
          isFormTypeAvailable(availableForms, 'tr'),
        rip:
          !!isActionEnabled(allowedActions, 'create-rip') &&
          isFormTypeAvailable(availableForms, 'rip'),
        tar:
          !!isActionEnabled(allowedActions, 'create-tar') &&
          isFormTypeAvailable(availableForms, 'tar'),
        pnr:
          !!isActionEnabled(allowedActions, 'create-pnr') &&
          isFormTypeAvailable(availableForms, 'pnr'),
      };
      setisViewFormsEnabled(formsActions);
    }
  }, [allowedActions, availableForms, isForm]);

  return (
    <React.Fragment>
      {isViewPanelOpen && (
        <ViewPanelContainer sx={expandedPanel ? { flex: 1 } : undefined}>
          <Header />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              p: 2,
              gap: 2,
              overflow: 'auto',
            }}
          >
            {isForm &&
              isViewFormsEnabled[
                deliverableAbbreviation as keyof TFormsActions
              ] && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Link
                    underline="hover"
                    variant="body2"
                    onClick={() =>
                      navigate(
                        `/${formViewRouteByAbbreviation[deliverableAbbreviation] ?? deliverableAbbreviation}/${data?.deliverableId}?mode=view`,
                      )
                    }
                    sx={{
                      color: '#0092CB',
                      fontWeight: 500,
                      p: 0,
                      textTransform: 'none',
                    }}
                  >
                    View Form
                  </Link>
                </Box>
              )}
            <ViewPanelContentContainer>
              <DeliverableDetailsContainer>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle2">Description:</Typography>
                  <Typography variant="body1">
                    {data?.description || '-'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle2">Due Date:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {data?.dueDate
                      ? dayjs(data.dueDate)
                          .utc()
                          .format('DD MMM YYYY')
                          .toString()
                      : '-'}
                  </Typography>
                </Box>
                {!isForm && (
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2">Location:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {data?.location?.name || '-'}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle2">Date Submitted:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {data?.dateSubmitted
                      ? dayjs(data.dateSubmitted)
                          .utc()
                          .format('DD MMM YYYY')
                          .toString()
                      : '-'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle2">Author:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {data?.author?.name || '-'}
                  </Typography>
                </Box>
              </DeliverableDetailsContainer>
              <Downloads
                attachments={[...(data?.attachments ?? [])].sort((a, b) =>
                  (a?.name ?? '').localeCompare(b?.name ?? '', undefined, {
                    numeric: true,
                    sensitivity: 'base',
                  }),
                )}
                zipFileName={`${data?.deliverableTitle}_attachments`}
              />
            </ViewPanelContentContainer>
          </Box>
        </ViewPanelContainer>
      )}
    </React.Fragment>
  );
};

export default ViewPanel;
